import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import {BenchlingState} from 'redux-stores/reduceApp';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import LegacyDropdownMenu from 'ui-library/dropdowns/LegacyDropdownMenu';
import * as RichObjectTable from 'ui-library/RichObjectTable';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import * as StageRunActions from 'workflows/StageRunActions';
import {getOtherParentOutputSamples} from 'workflows/util/StageRunUtil';

import {
  STAGE_RUN_FULL_SHAPE,
  STAGE_RUN_SAMPLE_FULL_SHAPE,
  StageRun,
  StageRunSample,
} from '../../WorkflowConstants';
import ActionCell from './ActionCell';

interface ConnectedProps {
  parentStageRuns: Array<StageRun>;
}

function mapStateToProps(
  state: BenchlingState,
  {eligibleParentStageRunIds}: BaseSelectParentStageRunSampleCellProps
): ConnectedProps {
  const parentStageRuns = eligibleParentStageRunIds
    .map((parentId) => new WorkflowSelector(state.models.workflows).getStageRun(parentId))
    .filter((parent) => parent) as Array<StageRun>;
  return {
    parentStageRuns,
  };
}

/**
 * Component for selecting one of the parent's outputs as the input to the current stage run.
 */
function SelectParentStageRunSampleCell(
  props: BaseSelectParentStageRunSampleCellProps & ConnectedProps
): JSX.Element {
  const {
    children,
    stageRun,
    parentStageRuns,
    stageRunSampleById,
    locked,
    isDropdownOpen,
    onOpenDropdown,
    onCloseDropdown,
  } = props;
  if (locked) {
    return <RichObjectTable.Cell primary={true}>{children}</RichObjectTable.Cell>;
  }

  // eslint-disable-next-line no-restricted-syntax
  const otherParentOutputSamples = _.flatten(
    parentStageRuns.map((parentStageRun) =>
      getOtherParentOutputSamples(stageRun, {parentStageRun, stageRunSampleById})
    )
  );

  return (
    <AbsoluteDropdownContainer
      menu={
        <LegacyDropdownMenu
          items={[
            otherParentOutputSamples.map((sample) => ({
              name: sample.name,
              handleOnClick: () => {
                StageRunActions.replaceInputSample(stageRun.id, {
                  sampleIdToAdd: sample.id,
                });
              },
            })),
          ]}
        />
      }
      onRequestClose={onCloseDropdown}
      open={isDropdownOpen}
      shouldCloseOnInsideClick={true}
    >
      <RichObjectTable.Cell primary={true}>
        <ActionCell
          icon="chevron-down"
          onClick={onOpenDropdown}
          isIconVisible={otherParentOutputSamples.length > 0}
        >
          {children}
        </ActionCell>
      </RichObjectTable.Cell>
    </AbsoluteDropdownContainer>
  );
}

interface BaseSelectParentStageRunSampleCellProps {
  eligibleParentStageRunIds: Array<string>;
  children: JSX.Element;
  stageRun: StageRun;
  stageRunSampleById: {[id: string]: StageRunSample};
  locked: boolean;
  isDropdownOpen: boolean;
  onOpenDropdown: () => void;
  onCloseDropdown: () => void;
}

SelectParentStageRunSampleCell.propTypes = {
  // Parents whose outputs should be presented in the dropdown.
  eligibleParentStageRunIds: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,

  children: PropTypes.node.isRequired,
  stageRun: STAGE_RUN_FULL_SHAPE.isRequired,
  stageRunSampleById: PropTypes.objectOf(STAGE_RUN_SAMPLE_FULL_SHAPE).isRequired,
  locked: PropTypes.bool.isRequired,
  isDropdownOpen: PropTypes.bool.isRequired,
  onOpenDropdown: PropTypes.func.isRequired,
  onCloseDropdown: PropTypes.func.isRequired,

  // Props provided by connect
  parentStageRuns: PropTypes.arrayOf(STAGE_RUN_FULL_SHAPE.isRequired).isRequired,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any;

export default connect(mapStateToProps)(SelectParentStageRunSampleCell);
